import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert, makeStyles, Button, FormControl, FormHelperText, Paper, Typography } from '@ellucian/react-design-system/core';
import {
    spacing60, spacing40, spacing20, spacing30, spacing50, spacing80, boxShadowError, fontSizeLarge,
    borderWidthThick, colorBorderAlertError, borderRadiusXSmall
} from '@ellucian/react-design-system/core/styles/tokens';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import { useApi } from '../ApiContext';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { clearForm, clearForceShowErrors, Form, FormTextField, setValue } from '@nsr/react-form';
import { useSelector, batch, useDispatch } from 'react-redux';
import retrievalReducer from './retrievalSlice';
import useRecaptcha from '../Helpers/useRecaptcha';
import { FormattedMessage } from 'react-intl';
import UAEPassButton from '../UAEPass/UAEPassButton';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import { validateEmail as sharedValidateEmail } from '@nsr/applicant-apply-shared/src/helpers/emailValidators';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import DOMPurify from 'dompurify';
import useMaintenanceStatus from '../Helpers/useMaintenanceStatus';
import { getMaintenanceMessages } from '../Helpers/maintenanceMessageHelpers';

const useStyles = makeStyles({
    name: 'RetrievalForm'
})((theme) => {
    return ({
        root: {
            padding: spacing40,
            margin: `${spacing80} auto 0 auto`,
            maxWidth: '800px',
            [theme.breakpoints.up('md')]: {
                padding: spacing50
            },
            [theme.breakpoints.up('lg')]: {
                padding: spacing60
            },
        },
        content: {
            marginTop: spacing40,
            padding: spacing40,
            overflowWrap: 'break-word',
            [theme.breakpoints.up('md')]: {
                padding: spacing50
            },
            [theme.breakpoints.up('lg')]: {
                padding: spacing60
            },
        },
        formTitle: {
            marginBottom: spacing20,
            [theme.breakpoints.up('md')]: {
                marginBottom: spacing30
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: spacing40
            },
        },
        detailsText: {
            marginBottom: spacing40,
            [theme.breakpoints.up('md')]: {
                marginBottom: spacing50
            },
        },
        standardField: {
            marginBottom: spacing40,
            [theme.breakpoints.up('md')]: {
                marginBottom: spacing50
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: spacing60
            },
        },
        captchaContainer: {
            display: 'flex',
            flexDirection: 'row-reverse',
            marginBottom: spacing40,
        },
        notVerified: {
            border: `${borderWidthThick} solid ${colorBorderAlertError}`,
            borderRadius: borderRadiusXSmall,
            boxShadow: boxShadowError,
            padding: spacing20,
            display: 'inline-block',
            [theme.breakpoints.up('md')]: {
                padding: spacing40
            },
        },
        continueButton: {
            display: 'flex',
            justifyContent: 'flex-end',
            flex: '0 0 auto'
        },
        actionRow: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: spacing20,
            marginTop: spacing20,
            width: '100%'
        },
        maintenanceBanner: {
            marginBottom: spacing40,
        },
        maintenancePage: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 16rem)',
            textAlign: 'center',
        },
        maintenanceMessage: {
            fontSize: fontSizeLarge,
            lineHeight: 1.6,
        },
        maintenanceSchedule: {
            marginTop: spacing40,
        },
    });
});

const RetrievalWrapper = props => {
    const additionalSlices = useRef({
        retrieval: retrievalReducer,
    });
    return (
        <Form createNewStore={true} additionalSlices={additionalSlices}>
            <RetrievalForm {...props} />
        </Form>
    );
};
const RetrievalForm = props => {
    const intl = useIntl();
    const { isTimedout } = props;
    const { classes } = useStyles();
    const api = useApi();
    const history = useHistory();
    const snackbar = useSnackbar();
    const dispatch = useDispatch();
    const [captchaError, setCaptchaError] = useState(false);
    const { communicationTypes, inquiryTypes, recaptchaActions } = CONSTANTS;
    const { globalLocale } = useContext(GlobalLocaleContext);
    const { globalSettings } = window.PRELOADED_STATE;
    const { recaptcha } = globalSettings;
    const { maintenanceStatus, status } = useMaintenanceStatus(globalSettings.maintenanceStatus);
    const {
        maintenanceMessage,
        warningBannerMessage,
    } = getMaintenanceMessages(intl, maintenanceStatus);
    const uaepassEnabled = globalSettings?.uaepassSettings?.enabled === true
        && window.PRELOADED_STATE?.featureSupported?.[SUPPORTED_FEATURES.uaepass] === true;
    const { getRecaptchaToken } = useRecaptcha(!recaptcha?.recaptchaToggle, recaptcha?.recaptchaClientToken);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const uaePassErrorMessageId = window.PRELOADED_STATE?.uaePassError?.messageId;

    const validateEmail = (value) => {
        const result = sharedValidateEmail(value);
        return !result.isValid ? intl.formatMessage({ id: 'invalidEmailFormatTextId' }) : undefined;
    };

    useEffect(() => {
        if (isTimedout) {
            batch(() => {
                dispatch(clearForceShowErrors());
                dispatch(clearForm());
            });
        }
    }, [isTimedout]);

    useEffect(() => {
        if (uaePassErrorMessageId) {
            snackbar.error(intl.formatMessage({ id: uaePassErrorMessageId }));
            window.PRELOADED_STATE.uaePassError = undefined;
        }
    }, [uaePassErrorMessageId, intl, snackbar]);

    const onSubmit = async values => {
        setIsSubmitting(true);
        const model = {
            email: sharedValidateEmail(currentEmail || '').normalizedEmail || currentEmail || '',
            phoneNumber: values[communicationTypes.text] || '',
            status: inquiryTypes.app,
            locale: globalLocale
        };

        const token = await getRecaptchaToken(recaptchaActions.retrieve);
        if (!token && recaptcha?.recaptchaToggle) {
            setIsSubmitting(false);
            setCaptchaError(true);
            return;
        }
        try {
            const params = new URLSearchParams(window.location.search);
            const bypass = params.get('bypass') === 'true';
            await api.inquiries.retrieve(model, token, recaptchaActions.retrieve, bypass);
            const state = {
                inquiry: {
                    status: inquiryTypes.app,
                },
                prospect: model,
            };
            const location = {
                pathname: routes.retrievalSubmitted,
                state: state
            };
            history.replace(location);
        } catch (e) {
            let msg = '';
            if (e.response) {
                const errorData = e.response.data;
                if (errorData.msg) {
                    msg = e.response.data.msg;
                } else {
                    msg = <FormattedMessage id="retrievalRecaptchaThresholdError" values={{ score: errorData.score, threshold: errorData.threshold }} />;
                }
                snackbar.error(msg);
            } else {
                msg = e.errorText;
                if (!msg && e.formatMessageId) {
                    msg = <FormattedMessage id={e.formatMessageId} />;
                }

                // Show a generic error message for string-based errors to prevent revealing technical details (e.g., unverified sender email)
                if (msg && typeof msg === 'string' && msg !== '') {
                    snackbar.error(intl.formatMessage({ id: 'adminGeneralErrorMessage' }));
                    return;
                }
            }
            snackbar.error(msg || <FormattedMessage id={'adminGeneralErrorMessage'} />);
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectErrors = state => state.form.errors;
    const errors = useSelector(selectErrors);

    const selectValue = state => state.form.values[communicationTypes.email];
    const currentEmail = useSelector(selectValue);

    const handleEmailBlur = useCallback((event) => {
        let escapedValue = DOMPurify.sanitize(event.target.value?.trim());
        if (escapedValue) {
            escapedValue = sharedValidateEmail(escapedValue).normalizedEmail || escapedValue;
        }
        dispatch(
            setValue({
                name: communicationTypes.email,
                value: escapedValue
            })
        );
    }, [dispatch, communicationTypes.email]);

    return (
        <div className={classes.root}>
            <Paper className={classes.content}>
                {status === CONSTANTS.maintenanceStatus.ACTIVE ?
                    <main className={classes.maintenancePage} role='main'>
                        <Typography variant="h1" className={classes.formTitle}>
                            <FormattedMessage id="maintenancePageTitle" />
                        </Typography>
                        {maintenanceMessage && <div className={classes.maintenanceMessage}><HtmlFormattedMessage htmlString={maintenanceMessage} normalizeSpaces /></div>}
                    </main> :
                    <main role='main'>
                        {status === CONSTANTS.maintenanceStatus.WARNING_WINDOW && warningBannerMessage &&
                        <div className={classes.maintenanceBanner}>
                            <Alert alertType="warning" open={true} text={warningBannerMessage} userDismissable={false} />
                        </div>
                        }
                        <div>
                            <Typography variant="h1" className={classes.formTitle}>
                                <strong><FormattedMessage id={'applicationRetrievalName'} /></strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className={classes.detailsText}>
                                <span><FormattedMessage id={'applicationRetrievalInstructions'} /></span>
                            </Typography>
                            <Typography variant="body1" className={classes.standardField}>
                                <HtmlFormattedMessage descriptor={{ id: 'applicationRetrievalDescription' }} normalizeSpaces />

                            </Typography>
                            <div className={classes.standardField}>
                                <FormControl component="fieldset" fullWidth>
                                    <div className={classes.standardField}>
                                        <FormTextField
                                            name={communicationTypes.email}
                                            fullWidth={true}
                                            label={intl.formatMessage({ id: 'applicationRetrievalEmailLabel' })}
                                            validate={validateEmail}
                                            required
                                            requiredErrorTextId={intl.formatMessage({ id: 'requiredFieldErrorTextId' })}
                                            onBlur={handleEmailBlur}
                                        />
                                    </div>
                                    <div className={classes.captchaContainer}>
                                        {captchaError ? <FormHelperText error={true}><FormattedMessage id="captchaValidationError" /></FormHelperText> : null}
                                    </div>
                                </FormControl>
                                <div className={classes.actionRow}>
                                    {uaepassEnabled && (
                                        <UAEPassButton
                                            variant="outline"
                                            href={routes.uaeLogin}
                                        />
                                    )}
                                    <Button
                                        appearance="primary"
                                        size="large"
                                        style={{ height: '52px', borderRadius: '27px' }}
                                        onClick={onSubmit}
                                        type='submit'
                                        disabled={isSubmitting || (errors?.email ? true : errors?.email ? true : false)}
                                    >
                                        <FormattedMessage id="continue" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </main>
                }
            </Paper>
        </div>
    );
};

export default RetrievalWrapper;
